import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Contenedor que se re-renderiza con el HTML nuevo cuando cambia la variante.
 * Uso: <cd-live data-cd-live="{{ block.id }}">...</cd-live>
 */
class CdLive extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.html) this.applyHtml(detail.html);
      })
      .catch(() => {});
  };

  /** Sustituye el contenido por el de la misma pieza en un HTML re-renderizado. */
  applyHtml(html) {
    const next = html.querySelector(`[data-cd-live="${this.dataset.cdLive}"]`);
    if (!next) return;
    this.innerHTML = next.innerHTML;
    this.refreshed();
  }

  refreshed() {}
}

/**
 * Ofertas por cantidad: al elegir una opción, pone esa cantidad en el formulario de compra.
 * Con "Elegir variante por unidad", cada unidad tiene sus propios selectores (talla, color…)
 * y se añaden al carrito como líneas separadas.
 */
class CdBundles extends CdLive {
  /** @type {number[]} variante elegida para cada unidad */
  picks = [];

  connectedCallback() {
    super.connectedCallback();
    this.product = this.readProduct();
    this.addEventListener('change', this.onChange);
    this.selected = this.querySelector('.cd-offer__input:checked')?.value;
    this.apply();
  }

  readProduct() {
    try {
      return JSON.parse(this.querySelector('[data-cd-product]')?.textContent || 'null');
    } catch (e) {
      return null;
    }
  }

  get currentVariantId() {
    return Number(this.querySelector('[data-cd-current-variant]')?.dataset.cdCurrentVariant);
  }

  get perUnit() {
    return this.dataset.perUnit === 'true' && this.product?.variants?.length > 1;
  }

  get quantity() {
    const input = this.querySelector('.cd-offer__input:checked');
    return input instanceof HTMLInputElement ? Number(input.dataset.quantity) || 1 : 1;
  }

  onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name.startsWith('cd-bundle')) {
      this.selected = target.value;
      this.apply();
    } else if (target instanceof HTMLSelectElement && target.dataset.unit) {
      this.updatePick(Number(target.dataset.unit));
    }
  };

  refreshed() {
    const input = this.querySelector(`.cd-offer__input[value="${this.selected}"]`);
    if (input instanceof HTMLInputElement) input.checked = true;
    // Horizon puede re-renderizar el selector de cantidad tras el cambio de variante.
    setTimeout(() => this.apply(), 0);
  }

  apply() {
    const input = this.querySelector('.cd-offer__input:checked');
    if (!(input instanceof HTMLInputElement)) return;

    const quantity = String(this.quantity);
    const form = this.section?.querySelector('product-form-component');
    const selector = form?.querySelector('quantity-selector-component');

    if (selector && typeof selector.setValue === 'function') {
      selector.setValue(quantity);
    } else {
      const field = form?.querySelector('input[name="quantity"]');
      if (field instanceof HTMLInputElement) field.value = quantity;
    }

    this.renderUnits();
  }

  /* ---------- Selectores por unidad ---------- */

  renderUnits() {
    this.querySelector('.cd-units')?.remove();
    const single = this.quantity < 2;
    if (!this.perUnit || (single && this.dataset.singleUnit !== 'true')) return;

    const { options, variants } = this.product;
    const fallback = variants.find((v) => v.id === this.currentVariantId) || variants.find((v) => v.available) || variants[0];
    const label = this.dataset.unitLabel || '';

    const wrap = document.createElement('div');
    wrap.className = single ? 'cd-units cd-units--single' : 'cd-units';

    for (let i = 0; i < this.quantity; i++) {
      if (!variants.some((v) => v.id === this.picks[i])) this.picks[i] = fallback.id;
      const variant = variants.find((v) => v.id === this.picks[i]);

      const row = document.createElement('div');
      row.className = 'cd-units__row';
      row.dataset.row = String(i);

      if (variants.some((v) => v.image)) {
        const thumb = document.createElement('img');
        thumb.className = 'cd-units__thumb';
        thumb.alt = '';
        thumb.width = 44;
        thumb.height = 44;
        if (variant.image) thumb.src = variant.image;
        row.append(thumb);
      }

      const name = document.createElement('span');
      name.className = 'cd-units__label';
      if (label) {
        const word = document.createElement('span');
        word.className = 'cd-units__word';
        word.textContent = `${label} `;
        name.append(word);
      }
      name.append(String(i + 1));
      if (!single) row.append(name);

      options.forEach((option, index) => {
        const optionName = typeof option === 'string' ? option : option.name;
        const values = [...new Set(variants.map((v) => v.options[index]))];
        const select = document.createElement('select');
        select.className = 'cd-units__select';
        select.dataset.unit = String(i);
        select.dataset.option = String(index);
        select.setAttribute('aria-label', `${name.textContent} – ${optionName}`);
        for (const value of values) {
          const text = options.length > 0 && optionName ? `${optionName}: ${value}` : value;
          const opt = new Option(text, value, false, variant.options[index] === value);
          select.append(opt);
        }
        row.append(select);
      });

      const error = document.createElement('span');
      error.className = 'cd-units__error';
      row.append(error);
      wrap.append(row);
    }

    this.querySelector('.cd-offer__input:checked')?.closest('.cd-offer-wrap')?.after(wrap);
    this.picks.length = this.quantity;
    for (let i = 0; i < this.quantity; i++) this.validateRow(i);

    if (this.picks[0] && this.picks[0] !== this.currentVariantId) this.setActiveVariant(this.picks[0]);
  }

  updatePick(unit) {
    const row = this.querySelector(`.cd-units__row[data-row="${unit}"]`);
    if (!row) return;
    const chosen = [...row.querySelectorAll('select')].map((s) => /** @type {HTMLSelectElement} */ (s).value);
    const match = this.product.variants.find((v) => v.options.every((value, index) => value === chosen[index]));
    this.picks[unit] = match ? match.id : NaN;

    // Miniatura: la de la variante elegida o, si la combinación no existe, la de otra variante del mismo valor
    const thumb = row.querySelector('.cd-units__thumb');
    const imageSource = match?.image ? match : this.product.variants.find((v) => v.image && v.options[0] === chosen[0]);
    if (thumb instanceof HTMLImageElement && imageSource?.image) thumb.src = imageSource.image;

    const valid = this.validateRow(unit);
    if (unit === 0 && valid && match) this.setActiveVariant(match.id);
  }

  /**
   * Cambia la variante activa sin el selector nativo: actualiza el formulario de compra,
   * la URL y los bloques que dependen del precio (precio, ofertas…).
   */
  async setActiveVariant(id) {
    if (!id || id === this.currentVariantId) return;

    const form = this.section?.querySelector('product-form-component form');
    const idInput = form?.querySelector('input[name="id"]');
    if (idInput instanceof HTMLInputElement) idInput.value = String(id);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(id));
    window.history.replaceState(window.history.state, '', url);

    const sectionId = this.section?.id?.replace('shopify-section-', '');
    if (!sectionId) return;

    try {
      const response = await fetch(`${url.pathname}?variant=${id}&section_id=${encodeURIComponent(sectionId)}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.section.querySelectorAll('cd-live, cd-bundles').forEach((el) => {
        if (typeof el.applyHtml === 'function') el.applyHtml(html);
      });
    } catch (e) {
      // Si falla la actualización visual, el formulario ya tiene la variante correcta.
    }
  }

  validateRow(unit) {
    const row = this.querySelector(`.cd-units__row[data-row="${unit}"]`);
    const error = row?.querySelector('.cd-units__error');
    if (!error) return true;
    const variant = this.product.variants.find((v) => v.id === this.picks[unit]);
    let message = '';
    if (!variant) message = this.dataset.textUnavailable || 'No disponible';
    else if (!variant.available) message = this.dataset.textSoldOut || 'Agotado';
    error.textContent = message;
    row.classList.toggle('cd-units__row--error', message !== '');
    return message === '';
  }

  /**
   * Devuelve las líneas a añadir si hay unidades con variantes distintas,
   * 'invalid' si alguna unidad no está disponible, o null para usar el flujo normal del tema.
   */
  getCartItems() {
    if (!this.perUnit || !this.querySelector('.cd-units')) return null;

    let valid = true;
    for (let i = 0; i < this.quantity; i++) valid = this.validateRow(i) && valid;
    if (!valid) return 'invalid';

    const counts = new Map();
    for (const id of this.picks.slice(0, this.quantity)) counts.set(id, (counts.get(id) || 0) + 1);
    if (counts.size < 2) return null;
    return [...counts].map(([id, quantity]) => ({ id, quantity }));
  }

  async addItems(productForm, items) {
    const button = productForm.querySelector('button[type="submit"], .add-to-cart-button');
    button?.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    productForm.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());

      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      const didError = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: productForm.id?.toString() || '',
          itemCount,
          productId: productForm.dataset.productId,
          sections: response.sections,
          didError,
        },
      });

      if (didError) {
        productForm.dispatchEvent(
          new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' })
        );
        this.showError(response.description || response.message);
      }
    } catch (error) {
      deferred.reject(error);
      this.showError(this.dataset.textSoldOut || 'Error');
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  showError(message) {
    const wrap = this.querySelector('.cd-units');
    if (!wrap || !message) return;
    let note = wrap.querySelector('.cd-units__global-error');
    if (!note) {
      note = document.createElement('p');
      note.className = 'cd-units__global-error';
      wrap.append(note);
    }
    note.textContent = message;
  }
}

/**
 * "Comprar ahora": añade las líneas y va directo al checkout (opcionalmente vaciando antes el carrito).
 */
async function cdBuyNow(productForm, lines, clearCart, bundles) {
  const button = productForm.querySelector('.add-to-cart-button, button[type="submit"]');
  button?.setAttribute('aria-busy', 'true');
  button?.setAttribute('disabled', '');
  const root = window.Shopify?.routes?.root || '/';

  try {
    if (clearCart) await fetch(`${root}cart/clear.js`, { method: 'POST' });
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines }),
    }).then((r) => r.json());

    if (response.status) throw new Error(response.description || response.message || 'Error');
    window.location.href = `${root}checkout`;
  } catch (error) {
    button?.removeAttribute('aria-busy');
    button?.removeAttribute('disabled');
    if (bundles instanceof CdBundles && bundles.querySelector('.cd-units')) bundles.showError(error.message);
    else window.alert(error.message);
  }
}

// Intercepta el envío del formulario de producto antes que el tema cuando hay
// unidades con variantes distintas, y las añade como líneas separadas.
if (!window.cdBundlesSubmitHook) {
  window.cdBundlesSubmitHook = true;
  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const productForm = form.closest('product-form-component');
      if (!productForm) return;

      const bundles = productForm.closest('.shopify-section, dialog')?.querySelector('cd-bundles');
      const items = bundles instanceof CdBundles ? bundles.getCartItems() : null;
      const direct = productForm.querySelector('[data-cd-direct-checkout]');

      if (items === 'invalid') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      if (direct) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const lines = items || [
          {
            id: Number(new FormData(form).get('id')),
            quantity: Number(new FormData(form).get('quantity')) || 1,
          },
        ];
        cdBuyNow(productForm, lines, direct.dataset.cdClearCart === 'true', bundles);
        return;
      }

      if (!items) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      bundles.addItems(productForm, items);
    },
    { capture: true }
  );
}

/**
 * Estimación de entrega: "¿Lo quieres el jueves, 1 oct? Pide en 3 h 20 min".
 */
class CdDelivery extends HTMLElement {
  connectedCallback() {
    this.render();
    this.timer = setInterval(() => this.render(), 30000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  addBusinessDays(date, days, skipWeekends) {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (!skipWeekends || (day !== 0 && day !== 6)) added++;
    }
    return result;
  }

  format(date) {
    const locale = this.dataset.locale || document.documentElement.lang || 'es';
    const text = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'short' }).format(date);
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /** Fin de la cuenta atrás por visitante (se guarda en el navegador y se renueva al acabar). */
  visitorDeadline(now) {
    const duration = Math.max(parseInt(this.dataset.countdownMinutes, 10) || 1, 1) * 60000;
    const key = this.dataset.key || 'cd-delivery';
    let stored = null;
    try {
      stored = parseInt(localStorage.getItem(key), 10);
    } catch (e) {}
    if (!stored || stored <= now.getTime()) {
      stored = now.getTime() + duration;
      try {
        localStorage.setItem(key, String(stored));
      } catch (e) {}
    }
    return new Date(stored);
  }

  render() {
    const now = new Date();
    const cutoffHour = parseInt(this.dataset.cutoff, 10) || 14;
    const skipWeekends = this.dataset.skipWeekends === 'true';
    let cutoff = new Date(now);
    cutoff.setHours(cutoffHour, 0, 0, 0);

    let start = now;
    if (this.dataset.mode === 'countdown') {
      cutoff = this.visitorDeadline(now);
    } else {
      const isWeekend = now.getDay() === 0 || now.getDay() === 6;
      if (now >= cutoff || (skipWeekends && isWeekend)) {
        start = this.addBusinessDays(now, 1, skipWeekends);
        cutoff.setTime(start.getTime());
        cutoff.setHours(cutoffHour, 0, 0, 0);
      }
    }

    const min = parseInt(this.dataset.minDays, 10) || 1;
    const max = parseInt(this.dataset.maxDays, 10) || min;
    const from = this.addBusinessDays(start, min, skipWeekends);
    const to = this.addBusinessDays(start, max, skipWeekends);
    const dateText = min === max ? this.format(from) : `${this.format(from)} - ${this.format(to)}`;

    const diff = Math.max(0, cutoff.getTime() - now.getTime());
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const timeText = `${hours} ${this.dataset.hoursLabel || 'h'} ${String(minutes).padStart(2, '0')} ${this.dataset.minutesLabel || 'min'}`;

    this.querySelectorAll('[data-cd-date]').forEach((el) => (el.textContent = dateText));
    this.querySelectorAll('[data-cd-time]').forEach((el) => (el.textContent = timeText));
  }
}

if (!customElements.get('cd-live')) customElements.define('cd-live', CdLive);
if (!customElements.get('cd-bundles')) customElements.define('cd-bundles', CdBundles);
if (!customElements.get('cd-delivery')) customElements.define('cd-delivery', CdDelivery);

// "Comprar ahora": la barra fija inferior muestra el mismo texto que el botón principal
function cdSyncStickyLabel() {
  const direct = document.querySelector('[data-cd-direct-checkout]');
  const sticky = document.querySelector('.sticky-add-to-cart__button');
  if (!direct || !sticky) return;
  const text = sticky.querySelector('.add-to-cart-text__content > span:first-child');
  if (text && direct.dataset.cdLabel) text.textContent = direct.dataset.cdLabel;
  sticky.querySelector('.add-to-cart-text > .svg-wrapper')?.setAttribute('hidden', '');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cdSyncStickyLabel);
else cdSyncStickyLabel();
